import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface GenerateVoiceoversActionProps {
  tourId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateVoiceoversAction({ tourId, open, onOpenChange }: GenerateVoiceoversActionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [voice, setVoice] = useState<'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'>('alloy');

  const generateVoiceoversMutation = useMutation({
    mutationFn: async (data: { voice: string }) => {
      return apiClient.post(`/tours/${tourId}/voiceovers`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Tour', 'detail', tourId] });
      onOpenChange(false);
      toast.success('Voiceover generated successfully');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to generate voiceover');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            generateVoiceoversMutation.mutate({ voice });
          }}
        >
          <DialogHeader>
            <DialogTitle>Generate Voiceover</DialogTitle>
            <DialogDescription>
              Generate a voiceover for this tour. This will generate a voiceover for the tour
              description using OpenAI text-to-speech.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="voice" className="block text-sm font-medium mb-1">
                Voice <span className="text-destructive">*</span>
              </label>
              <select
                id="voice"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voice}
                onChange={(e) =>
                  setVoice(
                    e.target.value as 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer',
                  )
                }
              >
                <option value="alloy">Alloy</option>
                <option value="echo">Echo</option>
                <option value="fable">Fable</option>
                <option value="onyx">Onyx</option>
                <option value="nova">Nova</option>
                <option value="shimmer">Shimmer</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={generateVoiceoversMutation.isPending}>
              Generate Voiceover
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Export the icon for use in the custom action
export const GenerateVoiceoversIcon = AudioLines;
